/**
 * Unified StreamClient — the single entry point for all streaming in the app.
 *
 * Handles both SSE (persistent, server-push) and NDJSON (request/response)
 * protocols through a consistent interface. Features:
 *
 * - **Protocol abstraction**: SSE and NDJSON share the same lifecycle
 * - **Configurable retry**: exponential, linear, or fixed backoff
 * - **Error classification**: user-initiated, recoverable, non-recoverable
 * - **Auth integration**: Bearer token injection with E2E test mode
 * - **Observability**: Sentry breadcrumbs and error captures
 * - **Timeout management**: per-attempt timeouts for NDJSON requests
 * - **Last-Event-ID**: automatic SSE resumption on reconnect
 * - **Abort-safe**: external signal support + internal AbortController
 *
 * Built on raw `fetch` — no third-party streaming dependencies.
 */

import { captureException, Sentry } from '@/modules/sentry';

import {
  type IStreamConfig,
  type IStreamCallbacks,
  type IStreamConnection,
  type IRetryConfig,
  type IMessageMeta,
  EStreamProtocol,
  EStreamState,
  EStreamErrorType,
} from './types';
import {
  classifyStreamError,
  STREAM_ABORT_REASON,
  STREAM_TIMEOUT_REASON,
  DEFAULT_FATAL_HTTP_STATUSES,
  RETRYABLE_HTTP_STATUSES,
} from './errors';
import {
  resolveRetryConfig,
  computeRetryDelay,
  shouldRetry,
  buildRetryInfo,
  waitWithAbort,
} from './retry';
import { SSEParser } from './parsers/sse-parser';
import { NDJSONParser } from './parsers/ndjson-parser';

/**
 * Creates and manages a streaming connection.
 *
 * @typeParam T - Type of parsed messages delivered to onMessage
 * @param config - Stream configuration
 * @param callbacks - Event callbacks
 * @returns Connection handle with state and close()
 *
 * @example
 * ```ts
 * const connection = createStream<MyEvent>(
 *   {
 *     url: '/api/events',
 *     protocol: EStreamProtocol.SSE,
 *     auth: { getToken: () => getAccessTokenSilently() },
 *     retry: { maxAttempts: 5, strategy: ERetryStrategy.EXPONENTIAL, baseDelayMs: 1000 },
 *     observability: { module: 'notifications', operation: 'stream_events' },
 *   },
 *   {
 *     onMessage: (event) => handleEvent(event),
 *     onStateChange: (state) => setConnectionState(state),
 *   },
 * );
 *
 * // Later:
 * connection.close();
 * ```
 */
export function createStream<T>(
  config: IStreamConfig<T>,
  callbacks: IStreamCallbacks<T>,
): IStreamConnection {
  const retryConfig = resolveRetryConfig(config.retry);
  const fatalStatuses = new Set(
    config.fatalHttpStatuses ?? DEFAULT_FATAL_HTTP_STATUSES,
  );
  const retryableStatuses = new Set([
    ...RETRYABLE_HTTP_STATUSES,
    ...(config.retryableHttpStatuses ?? []),
  ]);
  const parse = config.parse ?? ((raw: string) => JSON.parse(raw) as T);
  const defaultMethod =
    config.protocol === EStreamProtocol.NDJSON ? 'POST' : 'GET';
  const method = config.method ?? defaultMethod;
  const reconnectOnClose = config.reconnectOnClose ?? true;
  const reconnectCooldownMs = config.reconnectCooldownMs ?? 1000;

  let currentState = EStreamState.IDLE;
  let lastEventId = config.lastEventId;
  let abortController: AbortController | null = null;
  let closed = false;
  // Internal signal used to interrupt retry delays when close() is called
  // without an external signal. close() aborts this controller, and
  // waitWithAbort listens on it so the delay resolves immediately.
  const closeController = new AbortController();

  function setState(next: EStreamState): void {
    if (next === currentState) return;
    const prev = currentState;
    currentState = next;
    callbacks.onStateChange?.(next, prev);
  }

  /**
   * Emits a Sentry breadcrumb for an SSE lifecycle transition.
   *
   * Purely observational: Replay does not capture streaming response bodies in
   * its network tab, so these breadcrumbs surface SSE activity in Replay
   * timelines. Scoped to the SSE protocol so the `sse` category stays accurate.
   * Never throws into the streaming path.
   */
  function addSSEBreadcrumb(
    level: 'info' | 'warning' | 'error',
    message: string,
    data?: Record<string, unknown>,
  ): void {
    if (config.protocol !== EStreamProtocol.SSE) return;
    // Breadcrumbs are a purely observational side effect: a telemetry failure
    // (e.g. serializing event data) must never disrupt the stream or its
    // retry/reconnection control flow.
    try {
      Sentry.addBreadcrumb({
        category: 'sse',
        level,
        message,
        data: {
          url: config.url,
          ...(config.observability && {
            module: config.observability.module,
            operation: config.observability.operation,
          }),
          ...data,
        },
      });
    } catch {
      // Ignore — observability must not affect streaming behavior.
    }
  }

  function close(): void {
    if (closed) return;
    closed = true;
    closeController.abort(STREAM_ABORT_REASON);
    abortController?.abort(STREAM_ABORT_REASON);
    abortController = null;
    setState(EStreamState.CLOSED);
  }

  /**
   * Builds request headers including auth, custom headers, and SSE-specific headers.
   */
  async function buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    if (config.protocol === EStreamProtocol.SSE) {
      headers['Accept'] = 'text/event-stream';
      headers['Cache-Control'] = 'no-cache';
    } else {
      headers['Accept'] = 'application/x-ndjson';
      headers['Content-Type'] = 'application/json';
    }

    if (config.auth) {
      if (config.auth.e2eTestMode) {
        headers['X-E2E-Test-Mode'] = 'true';
      } else {
        try {
          const token = await config.auth.getToken();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch {
          // Auth may not be required in all environments
        }
      }
    }

    if (lastEventId && config.protocol === EStreamProtocol.SSE) {
      headers['Last-Event-ID'] = lastEventId;
    }

    if (config.headers) {
      Object.assign(headers, config.headers);
    }

    return headers;
  }

  /**
   * Reads and parses the response body as SSE events.
   */
  async function readSSE(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    signal: AbortSignal,
    onActivity?: () => void,
  ): Promise<T[]> {
    const decoder = new TextDecoder();
    const parser = new SSEParser();
    const results: T[] = [];

    while (!signal.aborted) {
      const { done, value } = await reader.read();

      if (done) break;

      // Reset the inactivity timeout on every chunk so long-running streams
      // (and SSE keepalive comments) are not killed before the server is
      // actually silent for the full timeoutMs window.
      onActivity?.();

      const chunk = decoder.decode(value, { stream: true });
      const events = parser.push(chunk);

      for (const sseEvent of events) {
        if (sseEvent.id) {
          lastEventId = sseEvent.id;
        }

        if (sseEvent.data) {
          try {
            const parsed = parse(sseEvent.data);
            const meta: IMessageMeta = {
              eventId: sseEvent.id,
              eventType: sseEvent.event,
            };

            if (callbacks.onComplete) {
              results.push(parsed);
            }

            addSSEBreadcrumb(
              'info',
              sseEvent.id
                ? `SSE message received ${sseEvent.id}`
                : 'SSE message received',
              {
                eventId: sseEvent.id,
                eventType: sseEvent.event,
                // Include the entire event for now.
                event: parsed,
              },
            );

            callbacks.onMessage(parsed, meta);
          } catch {
            // Malformed event data — skip
          }
        }
      }
    }

    return results;
  }

  /**
   * Reads and parses the response body as NDJSON.
   */
  async function readNDJSON(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    signal: AbortSignal,
    onActivity?: () => void,
  ): Promise<T[]> {
    const decoder = new TextDecoder();
    const parser = new NDJSONParser<T>();
    const results: T[] = [];
    // Line buffer for custom parse path: a network chunk can split a line
    // mid-way, so we carry the trailing incomplete fragment across reads.
    let lineBuffer = '';

    while (!signal.aborted) {
      const { done, value } = await reader.read();

      if (done) break;

      // Reset the inactivity timeout on every chunk so long-running streams
      // are not killed before the server is actually silent.
      onActivity?.();

      const chunk = decoder.decode(value, { stream: true });

      let objects: T[];
      if (config.parse) {
        // Prepend any leftover fragment from the previous chunk
        const text = lineBuffer + chunk;
        const segments = text.split('\n');
        // Last segment is either empty (chunk ended with \n) or an
        // incomplete line — keep it for the next iteration.
        lineBuffer = segments.pop() ?? '';
        objects = [];
        for (const segment of segments) {
          const trimmed = segment.trim();
          if (!trimmed) continue;
          try {
            objects.push(config.parse(trimmed));
          } catch {
            // Skip malformed lines
          }
        }
      } else {
        objects = parser.push(chunk);
      }

      for (const obj of objects) {
        const meta: IMessageMeta = {};

        if (callbacks.onComplete) {
          results.push(obj);
        }

        callbacks.onMessage(obj, meta);
      }
    }

    // Flush remaining buffer
    if (config.parse) {
      // Custom parser: flush any trailing data in the line buffer
      const trimmed = lineBuffer.trim();
      if (trimmed) {
        try {
          const parsed = config.parse(trimmed);

          if (callbacks.onComplete) {
            results.push(parsed);
          }

          callbacks.onMessage(parsed, {});
        } catch {
          // Skip malformed trailing data
        }
      }
    } else {
      const remaining = parser.flush();
      for (const obj of remaining) {
        if (callbacks.onComplete) {
          results.push(obj);
        }

        callbacks.onMessage(obj, {});
      }
    }

    return results;
  }

  /**
   * Single connection attempt. Returns results on success, throws on failure.
   */
  async function attempt(
    signal: AbortSignal,
    onActivity?: () => void,
  ): Promise<T[]> {
    const headers = await buildHeaders();
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

    const fetchInit: RequestInit = {
      method,
      headers,
      signal,
    };

    if (config.body !== undefined) {
      fetchInit.body = JSON.stringify(config.body);
    }

    const response = await fetch(config.url, fetchInit);

    if (!response.ok) {
      const httpError = new Error(
        `Stream HTTP error: ${response.status} ${response.statusText}`,
      );
      (httpError as Error & { status?: number }).status = response.status;
      throw httpError;
    }

    if (!response.body) {
      throw new Error('Response has no readable body');
    }

    setState(EStreamState.CONNECTED);
    addSSEBreadcrumb('info', 'SSE connection open', {
      status: response.status,
      lastEventId,
    });

    const reader = response.body.getReader();

    try {
      if (config.protocol === EStreamProtocol.SSE) {
        return await readSSE(reader, signal, onActivity);
      } else {
        return await readNDJSON(reader, signal, onActivity);
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        // Already released
      }
    }
  }

  /**
   * Connection loop with retry logic.
   * For SSE: reconnects on close (persistent connection).
   * For NDJSON: retries on transient errors (request/response).
   */
  async function connectLoop(): Promise<void> {
    let consecutiveFailures = 0;

    // Link to external signal once, outside the retry loop
    if (config.signal) {
      if (config.signal.aborted) {
        close();
        return;
      }
      config.signal.addEventListener(
        'abort',
        () => {
          closed = true;
          abortController?.abort(STREAM_ABORT_REASON);
          setState(EStreamState.CLOSED);
        },
        { once: true },
      );
    }

    while (!closed) {
      abortController = new AbortController();
      const signal = abortController.signal;

      // Inactivity timeout: armed before the request and reset on every
      // chunk received from the server (via the onActivity callback passed
      // into attempt()). Fires only when the server is silent for the full
      // timeoutMs window, not when a long stream is still active.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const armTimeout = (): void => {
        if (!config.timeoutMs) return;
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          abortController?.abort(STREAM_TIMEOUT_REASON);
        }, config.timeoutMs);
      };
      armTimeout();

      setState(
        consecutiveFailures === 0
          ? EStreamState.CONNECTING
          : EStreamState.RECONNECTING,
      );

      try {
        const results = await attempt(signal, armTimeout);

        if (timeoutId) clearTimeout(timeoutId);

        // Stream ended normally
        consecutiveFailures = 0;
        callbacks.onComplete?.(results);

        // SSE: reconnect on normal close if configured
        if (
          config.protocol === EStreamProtocol.SSE &&
          reconnectOnClose &&
          !closed
        ) {
          addSSEBreadcrumb('warning', 'SSE reconnecting after stream close', {
            cooldownMs: reconnectCooldownMs,
            lastEventId,
          });
          // Cooldown prevents tight reconnection loops when the server
          // closes connections rapidly (e.g. HTTP/2 protocol errors)
          if (reconnectCooldownMs > 0) {
            await waitWithAbort(reconnectCooldownMs, config.signal);
            if (closed) return;
          }
          continue;
        }

        // NDJSON or SSE without reconnect: done
        if (!closed) {
          setState(EStreamState.CLOSED);
        }
        return;
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);

        if (closed || signal.aborted) {
          const classified = classifyStreamError(error, signal);
          if (classified.type === EStreamErrorType.USER_INITIATED) {
            return;
          }
        }

        // Check for fatal HTTP status
        const httpStatus = (error as Error & { status?: number }).status;
        if (httpStatus && fatalStatuses.has(httpStatus)) {
          const streamError = classifyStreamError(error, signal);
          streamError.httpStatus = httpStatus;
          streamError.type = EStreamErrorType.NON_RECOVERABLE;
          streamError.reason = `fatal-http-${httpStatus}`;
          addSSEBreadcrumb('error', 'SSE fatal HTTP error', {
            httpStatus,
            reason: streamError.reason,
          });
          callbacks.onError?.(streamError);
          reportToSentry(error, retryConfig, consecutiveFailures, 'fatal-http');
          if (!closed) setState(EStreamState.FAILED);
          return;
        }

        consecutiveFailures++;
        const classified = classifyStreamError(error, signal);

        // A non-2xx response classifies as 'unknown'; keep the status in the
        // reason so Sentry and breadcrumbs stay groupable by HTTP status.
        if (httpStatus !== undefined && classified.reason === 'unknown') {
          classified.reason = `http-${httpStatus}`;
        }

        // Check if retryable
        const isRetryable =
          classified.type === EStreamErrorType.RECOVERABLE ||
          (httpStatus !== undefined && retryableStatuses.has(httpStatus));

        if (isRetryable && shouldRetry(consecutiveFailures, retryConfig)) {
          const delayMs = computeRetryDelay(consecutiveFailures, retryConfig);
          const retryInfo = buildRetryInfo(
            consecutiveFailures,
            retryConfig,
            classified.reason,
          );
          addSSEBreadcrumb('warning', 'SSE reconnect/backoff attempt', {
            attempt: consecutiveFailures,
            maxAttempts: retryConfig.maxAttempts,
            delayMs,
            reason: classified.reason,
          });
          callbacks.onRetry?.(retryInfo);

          await waitWithAbort(delayMs, closeController.signal);

          if (closed) return;
          continue;
        }

        // Non-recoverable or exhausted retries
        classified.httpStatus = httpStatus;
        addSSEBreadcrumb('error', 'SSE stream error', {
          httpStatus,
          attempts: consecutiveFailures,
          reason: classified.reason,
        });
        callbacks.onError?.(classified);
        reportToSentry(
          error,
          retryConfig,
          consecutiveFailures,
          classified.reason,
        );
        if (!closed) setState(EStreamState.FAILED);
        return;
      }
    }
  }

  /**
   * Reports a streaming failure to Sentry.
   */
  function reportToSentry(
    error: unknown,
    retry: IRetryConfig,
    attempts: number,
    reason: string,
  ): void {
    if (!config.observability) return;

    captureException(
      error instanceof Error ? error : new Error(String(error)),
      {
        tags: {
          module: config.observability.module,
          action: config.observability.operation,
          stream_failure_reason: reason,
        },
        extra: {
          url: config.url,
          protocol: config.protocol,
          attempts,
          maxAttempts: retry.maxAttempts,
          ...config.observability.extra,
        },
      },
    );
  }

  // Start the connection
  void connectLoop();

  return {
    get state() {
      return currentState;
    },
    get lastEventId() {
      return lastEventId;
    },
    close,
  };
}
